/**
 * ClientLeadsTab — Lead delivery table + stats
 *
 * Uses `agent_status` for the Pipeline column (same field the agent dashboard uses).
 * Shows lead names from `lead_profile` when available.
 */

import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Search, Package, Target, BarChart3 } from "lucide-react";
import type { ClientLead, ClientContract } from "@/hooks/useAMClientDetail";

interface Props {
  leads: ClientLead[];
  contracts: ClientContract[];
}

// Match the agent dashboard pipeline styles exactly
const pipelineStyle: Record<string, { label: string; className: string }> = {
  new: { label: "New", className: "bg-blue-100 text-blue-700 border-blue-200" },
  contacted: { label: "Contacted", className: "bg-amber-100 text-amber-700 border-amber-200" },
  no_answer: { label: "No Answer", className: "bg-orange-100 text-orange-700 border-orange-200" },
  qualified: { label: "Qualified", className: "bg-purple-100 text-purple-700 border-purple-200" },
  not_qualified: { label: "Not Qualified", className: "bg-slate-100 text-slate-600 border-slate-200" },
  proposal_sent: { label: "Proposal Sent", className: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  won: { label: "Won", className: "bg-green-100 text-green-700 border-green-200" },
  lost: { label: "Lost", className: "bg-red-100 text-red-700 border-red-200" },
};

const defaultPipelineStyle = { label: "New", className: "bg-blue-100 text-blue-700 border-blue-200" };

function normalizeAgentStatus(raw: string | null): string {
  if (!raw || raw === "pending") return "new";
  return raw;
}

function getLeadName(profile: Record<string, unknown> | null): string {
  if (!profile) return "—";
  const first = (profile.first_name as string) || (profile.firstName as string) || "";
  const last = (profile.last_name as string) || (profile.lastName as string) || "";
  const full = (profile.full_name as string) || (profile.fullName as string) || (profile.name as string) || "";
  if (full) return full;
  if (first || last) return `${first} ${last}`.trim();
  return "—";
}

export default function ClientLeadsTab({ leads, contracts }: Props) {
  const [search, setSearch] = useState("");

  const activeContracts = contracts.filter((c) => c.status === "active");
  const totalCommitted = activeContracts.reduce((s, c) => s + c.total_leads_committed, 0);
  const totalDelivered = activeContracts.reduce((s, c) => s + c.leads_delivered, 0);

  const deliveryPct = totalCommitted > 0 ? Math.round((totalDelivered / totalCommitted) * 100) : 0;

  // Pipeline breakdown using agent_status
  const pipelineBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    leads.forEach((l) => {
      const status = normalizeAgentStatus(l.agent_status);
      map[status] = (map[status] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [leads]);

  // Contact rate
  const contacted = leads.filter((l) => l.agent_contacted_at).length;
  const contactRate = leads.length > 0 ? Math.round((contacted / leads.length) * 100) : 0;

  const filtered = search
    ? leads.filter((l) => {
        const q = search.toLowerCase();
        const name = getLeadName(l.lead_profile).toLowerCase();
        const pipeline = normalizeAgentStatus(l.agent_status);
        return (
          name.includes(q) ||
          l.lead_type_name.toLowerCase().includes(q) ||
          l.state.toLowerCase().includes(q) ||
          pipeline.includes(q)
        );
      })
    : leads;

  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">Delivery Progress</CardTitle>
            <Package className="h-3.5 w-3.5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalDelivered} / {totalCommitted}</div>
            <Progress value={deliveryPct} className="h-2 mt-2" />
            <p className="text-[10px] text-muted-foreground mt-1">{deliveryPct}% fulfilled</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">Contact Rate</CardTitle>
            <Target className="h-3.5 w-3.5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{contactRate}%</div>
            <p className="text-[10px] text-muted-foreground mt-1">
              {contacted} of {leads.length} contacted
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">Pipeline</CardTitle>
            <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1">
              {pipelineBreakdown.map(([status, count]) => {
                const style = pipelineStyle[status] ?? defaultPipelineStyle;
                return (
                  <Badge
                    key={status}
                    variant="outline"
                    className={`text-[10px] ${style.className}`}
                  >
                    {style.label}: {count}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leads Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Leads ({leads.length})</CardTitle>
            <div className="relative w-48">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Filter leads..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9 text-sm"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              {leads.length === 0 ? "No leads delivered yet." : "No leads match your filter."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-3 font-medium">Lead</th>
                    <th className="pb-3 font-medium">Type</th>
                    <th className="pb-3 font-medium">State</th>
                    <th className="pb-3 font-medium">Pipeline</th>
                    <th className="pb-3 font-medium text-right">Value</th>
                    <th className="pb-3 font-medium">Delivered</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 50).map((lead) => {
                    const agentStatus = normalizeAgentStatus(lead.agent_status);
                    const style = pipelineStyle[agentStatus] ?? defaultPipelineStyle;
                    const leadName = getLeadName(lead.lead_profile);

                    return (
                      <tr key={lead.id} className="border-b last:border-0">
                        <td className="py-2.5 font-medium">{leadName}</td>
                        <td className="py-2.5 text-muted-foreground">{lead.lead_type_name}</td>
                        <td className="py-2.5 font-mono text-xs">{lead.state}</td>
                        <td className="py-2.5">
                          <Badge variant="outline" className={`text-[10px] ${style.className}`}>
                            {style.label}
                          </Badge>
                        </td>
                        <td className="py-2.5 text-right font-mono text-xs">
                          ${lead.estimated_value.toFixed(2)}
                        </td>
                        <td className="py-2.5 text-muted-foreground text-xs">
                          {new Date(lead.created_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length > 50 && (
                <p className="text-xs text-muted-foreground text-center mt-3">
                  Showing 50 of {filtered.length} leads
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
